import {
  useQuery,
  QueryFilters,
  SetDataOptions,
  InvalidateOptions,
  InvalidateQueryFilters,
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
  Query,
  CancelOptions,
  QueryObserver,
} from "@tanstack/react-query";
import { APIResponseError } from "@web-monorepo/infra/responseHandlers";
import { produce } from "@web-monorepo/hooks";
import isEqual from "fast-deep-equal";
import { useMemo } from "react";
import { type ApiMethod, type ApiPath, APIResponse, EndpointQueryParameters, EndpointParams } from "./types";
import { makeApiErrorMessage } from "./error";
import { queryClient } from "./queryClient";
import { registerQuery, makeQueryKey } from "./queryKey";
import { mergeFns, useQueryScope } from "./queryUtils";
import { buildUrl } from "./urlBuilder";
import { buildHeaders } from "./headersBuilder";
import { urlPatternFromFetcher } from "./urlPatternFromFetcher";
import { NOOP, WAITING_FOR_DEPENDENCIES, CUSTOM_HEADERS } from "./constants";
import { apiFetch } from "./apiFetch";

export type { UseQueryResult };

/** Imperative query lifecycle for jobs whose number is only known after an action. */
export type MemberQueryObserver<MemberType> = Pick<
  QueryObserver<MemberType, Error>,
  "subscribe" | "getCurrentResult" | "destroy"
>;

export type MemberQueryParams<
  Path extends ApiPath,
  QueryParams extends keyof EndpointQueryParameters<Path>,
> = EndpointParams<Path, QueryParams> & {
  [CUSTOM_HEADERS]?: Record<string, string>;
};

type FullMemberQueryParams<Path extends ApiPath, QueryParams extends keyof EndpointQueryParameters<Path>> =
  | MemberQueryParams<Path, QueryParams>
  | typeof NOOP
  | typeof WAITING_FOR_DEPENDENCIES;

// React Query options exposed at the configuration level. Some options are
// facilitated by makeMemberQuery and some would cause unexpected behavior with
// the internal logic.
//
// Error-boundary behavior is implemented below rather than delegated to React Query.
// Only hook callers can opt out, so endpoint defaults remain unchanged.
type ExposedUseQueryOptions<TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, Error>,
  | "queryKey"
  | "queryFn"
  | "enabled"
  | "meta"
  | "queryKeyHashFn"
  | "select"
  | "useErrorBoundary"
  | "onSuccess"
  | "onError"
  | "onSettled"
>;

type EnhancedUseQueryOptions<MemberType, Params> = ExposedUseQueryOptions<MemberType> & {
  onSuccess?: (data: MemberType, params: Params) => void;
  onError?: (error: Error, params: Params) => void;
  onSettled?: (data: MemberType | undefined, error: Error | null, params: Params) => void;
  enabled?: boolean;
};

export type MemberQueryData<Path extends ApiPath> = "get" extends ApiMethod<Path> ? APIResponse<Path, "get"> : never;

// # Regarding variedHeaders:
// Sometimes an HTTP request header can influence the contents of the HTTP response.
// In those cases, the `Vary` response header should include a list of request headers
// that influenced the response content. Ideally, we'd use that to determine which
// request headers need to be a part of the React Query cacheKey, but React Query wants
// you to provide the cacheKey upfront before the request is ever made. Instead, we're
// maintaining a list here as this is an edge case currently.

export type MemberQueryConfig<
  Path extends ApiPath,
  QueryParams extends keyof EndpointQueryParameters<Path>,
  MemberType = MemberQueryData<Path>,
> = {
  fetcherName: string;
  path: Path;
  query?: Omit<EndpointQueryParameters<Path>, QueryParams>;
  queryParams?: QueryParams[];
  dontThrowOnStatusCodes?: number[];
  defaultHeaders?: Record<string, string>;
  variedHeaders?: string[];
  shouldFetchImmediately?: boolean;
  /**
   * Opt-in: fold selected response headers onto the returned member data. `apiFetch`
   * carries the response headers that this query would otherwise discard; supply a
   * mapper to surface the ones you need — e.g. a per-request flag that lives only in a
   * header, not the body. The mapped fields are spread over the body on success, so
   * choose keys that don't collide and widen `MemberType` to include them. Leaving
   * this unset changes nothing, so other fetchers are unaffected.
   */
  deriveFromHeaders?: (headers: Record<string, string>) => Partial<MemberType>;
} & EnhancedUseQueryOptions<MemberType, MemberQueryParams<Path, QueryParams>>;

export interface MemberQueryType<
  Path extends ApiPath,
  QueryParams extends keyof EndpointQueryParameters<Path>,
  MemberType = MemberQueryData<Path>,
> {
  (
    params: FullMemberQueryParams<Path, QueryParams>,
    options?: EnhancedUseQueryOptions<MemberType, MemberQueryParams<Path, QueryParams>> & {
      /** Handle HTTP and transport failures inline, retaining error/isError. */
      useErrorBoundary?: false;
    },
  ): UseQueryResult<MemberType, Error>;

  invalidateQueries(
    params?: Partial<MemberQueryParams<Path, QueryParams>>,
    filters?: Omit<InvalidateQueryFilters, "queryKey">,
    options?: InvalidateOptions,
  ): Promise<void>;

  shouldInvalidateQueries(
    shouldInvalidateQuery: (
      data: MemberType | undefined,
      params: MemberQueryParams<Path, QueryParams>,
    ) => boolean | null | undefined,
  ): void;

  cancelQueries(
    params?: Partial<MemberQueryParams<Path, QueryParams>>,
    filters?: Omit<QueryFilters, "queryKey">,
    options?: CancelOptions,
  ): Promise<void>;

  removeQueries(params?: Partial<MemberQueryParams<Path, QueryParams>>, filters?: Omit<QueryFilters, "queryKey">): void;

  getQueryData(params?: MemberQueryParams<Path, QueryParams>): MemberType | undefined;

  setQueryData(data: MemberType | null, params?: MemberQueryParams<Path, QueryParams>): void;

  getQueriesData(
    params?: Partial<MemberQueryParams<Path, QueryParams>>,
    filters?: Omit<InvalidateQueryFilters, "queryKey">,
  ): [QueryKey, MemberType | undefined][];

  setQueriesData(
    updater: ((draft: MemberType) => MemberType | void) | MemberType | null,
    params?: Partial<MemberQueryParams<Path, QueryParams>>,
    filters?: Omit<QueryFilters, "queryKey">,
    options?: SetDataOptions,
  ): void;

  /**
   * Imperatively warms the cache for `params` outside of React render, e.g. from a router
   * loader. Shares the exact query key the hook uses, so a subsequent hook call with the
   * same params is served from cache instead of refetching. Never throws or rejects — a
   * failed prefetch just leaves the cache empty and the hook fetches normally.
   */
  prefetch(params: MemberQueryParams<Path, QueryParams>): Promise<void>;

  /**
   * Observe outside React with the hook's typed endpoint, headers and cache key.
   * Subscribe to start fetching; destroy when the owner finishes. Failures stay in
   * the result for the owner to handle, since there is no React error boundary.
   * Destroy detaches this observer without removing other callers' cached data.
   */
  observe(
    params: MemberQueryParams<Path, QueryParams>,
    options?: EnhancedUseQueryOptions<MemberType, MemberQueryParams<Path, QueryParams>>,
  ): MemberQueryObserver<MemberType>;
}

export function makeMemberQuery<
  Path extends ApiPath,
  QueryParams extends keyof EndpointQueryParameters<Path> = never,
  MemberType = MemberQueryData<Path>,
>(config: MemberQueryConfig<Path, QueryParams, MemberType>): MemberQueryType<Path, QueryParams, MemberType> {
  const {
    path,
    query,
    queryParams,
    dontThrowOnStatusCodes = [],
    defaultHeaders,
    variedHeaders,
    deriveFromHeaders,
    fetcherName,
    onSuccess: onSuccessConfig,
    onError: onErrorConfig,
    onSettled: onSettledConfig,
    shouldFetchImmediately = true,
    ...extraConfig
  } = config;

  const urlPattern = urlPatternFromFetcher(path, query, queryParams);

  registerQuery(fetcherName);

  async function fetchMember(
    parsedUrl: string | undefined,
    headers: Record<string, string> | undefined,
  ): Promise<MemberType> {
    const response = await apiFetch<MemberType>({
      method: "GET",
      path: parsedUrl!,
      route: path,
      headers,
    });

    if (response.success) {
      if (deriveFromHeaders) {
        // Fold the opted-in response headers onto the member body. We use
        // Object.assign rather than a spread because `MemberType` is an
        // unconstrained generic (not `extends object`), so `{ ...response.data }`
        // is a compile error. Object.assign's return is inferred as the
        // intersection `{} & MemberType & Partial<MemberType>`, which TS won't
        // narrow back to `MemberType` on its own, so the `as MemberType` cast is
        // required. It is sound: both operands are already `MemberType` /
        // `Partial<MemberType>`, so no field outside `MemberType` is introduced.
        return Object.assign({}, response.data, deriveFromHeaders(response.headers)) as MemberType;
      }
      return response.data;
    }

    // TODO: Clean this up - I'm pretty sure we don't want to do this this way - I want to
    // eliminate the isExpected mechanism (we have three 'expected' concepts on errors,
    // server-side "expected", client-side "isExpected" (which is no longer used really),
    // and isExpectedError, which I need to hunt down to the ends of the earth and destroy
    // as well:
    const ex = response.error;
    if (ex instanceof APIResponseError) {
      ex.isExpected = dontThrowOnStatusCodes.includes(ex.response.statusCode);

      ex.message = makeApiErrorMessage({
        path: config.path,
        method: "GET",
        response: ex.response,
        name: fetcherName,
      });
    }
    throw ex;
  }

  function useQueryWrapper(
    params: FullMemberQueryParams<Path, QueryParams> = {} as MemberQueryParams<Path, QueryParams>,
    options: EnhancedUseQueryOptions<MemberType, MemberQueryParams<Path, QueryParams>> & {
      useErrorBoundary?: false;
    } = {},
  ) {
    const {
      onSuccess: onSuccessOption,
      onError: onErrorOption,
      onSettled: onSettledOption,
      useErrorBoundary,
      ...extraOptions
    } = options;
    const queryKey = useMemo(() => makeQueryKey({ fetcherName, params, variedHeaders }), [params]);

    // parsedUrl = f(urlPattern, params), e.g. "api/123/classes"
    // can be undefined if fetcher is a noop or if we're waiting on dependencies
    const { parsedUrl, headers } = useMemo(() => {
      if (typeof params !== "object") return { parsedUrl: undefined, headers: undefined };
      return {
        parsedUrl: buildUrl({ params, urlPattern }, true),
        headers: buildHeaders(defaultHeaders, params[CUSTOM_HEADERS]),
      };
    }, [params]);

    const awaitingDependencies = params === WAITING_FOR_DEPENDENCIES;
    const isNoop = params === NOOP;
    const enabled = shouldFetchImmediately && !isNoop && !awaitingDependencies && Boolean(parsedUrl);

    const { queryFn, onSuccess, onError, onSettled } = useQueryScope<MemberType>({
      queryFn: () => fetchMember(parsedUrl, headers),
      onSuccess: (data) => {
        onSuccessConfig?.(data, params as MemberQueryParams<Path, QueryParams>);
      },
      onError: (error) => {
        onErrorConfig?.(error, params as MemberQueryParams<Path, QueryParams>);
      },
      onSettled: (data, error) => {
        return onSettledConfig?.(data, error, params as MemberQueryParams<Path, QueryParams>);
      },
    });

    const result = useQuery<MemberType, Error>({
      queryFn,
      onSuccess: mergeFns(onSuccess, (data) => onSuccessOption?.(data, params as MemberQueryParams<Path, QueryParams>)),
      onError: mergeFns(onError, (error) => onErrorOption?.(error, params as MemberQueryParams<Path, QueryParams>)),
      onSettled: mergeFns(onSettled, (data, error) =>
        onSettledOption?.(data, error, params as MemberQueryParams<Path, QueryParams>),
      ),
      queryKey,
      enabled,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retryOnMount: false,
      staleTime: Infinity,
      retry: false,
      ...extraConfig,
      ...extraOptions,
    });

    // Failure policy. This throw happens *during render*, so a caller cannot catch it - the
    // nearest error boundary takes the subtree.
    //
    // `dontThrowOnStatusCodes` on the fetcher is what opts a status out, and it is site-wide: it
    // applies to every caller of this fetcher, forever. So it is the right home for a status that
    // means something about the *endpoint* - a 404 for a record that may not exist, a 401 pre-login
    // - where the same reading is correct for everyone. It is the wrong home for "this one screen
    // can cope without the data", which is a property of the screen, not the endpoint.
    //
    // A caller that renders its own failure state can set `useErrorBoundary: false`.
    // This suppresses only the render throw; HTTP and transport failures still retain
    // their original error/isError state. It does not change shared expected-status flags.
    // Without that opt-out, transport failures always throw because they have no HTTP status.
    //
    // Never opt a status out for an authorization or entitlement check, where absent data would
    // read as permission granted. See ai/rules/state-management.md.
    if (
      useErrorBoundary !== false &&
      !(result.error instanceof APIResponseError && result.error?.isExpected) &&
      result.error
    ) {
      throw result.error;
    }

    return result;
  }

  useQueryWrapper.shouldInvalidateQueries = (
    shouldInvalidateQuery: (
      data: MemberType | undefined,
      params: MemberQueryParams<Path, QueryParams>,
    ) => boolean | null | undefined,
  ): void => {
    const queriesData = queryClient.getQueriesData<MemberType>({
      queryKey: makeQueryKey({ fetcherName }),
    });
    queriesData.forEach(([queryKey, data]) => {
      const [, params] = queryKey as [string, MemberQueryParams<Path, QueryParams>];
      if (shouldInvalidateQuery(data, params)) {
        void useQueryWrapper.invalidateQueries(params);
      }
    });
  };

  useQueryWrapper.invalidateQueries = async (
    params?: Partial<MemberQueryParams<Path, QueryParams>>,
    filters?: Omit<InvalidateQueryFilters, "queryKey">,
    options?: InvalidateOptions,
  ): Promise<void> => {
    return queryClient.invalidateQueries({ queryKey: makeQueryKey({ fetcherName, params }), ...filters }, options);
  };

  useQueryWrapper.cancelQueries = async (
    params?: Partial<MemberQueryParams<Path, QueryParams>>,
    filters?: Omit<QueryFilters, "queryKey">,
    options?: CancelOptions,
  ): Promise<void> => {
    return queryClient.cancelQueries({ queryKey: makeQueryKey({ fetcherName, params }), ...filters }, options);
  };

  useQueryWrapper.removeQueries = (
    params?: Partial<MemberQueryParams<Path, QueryParams>>,
    filters?: Omit<QueryFilters, "queryKey">,
  ): void => {
    queryClient.removeQueries({ queryKey: makeQueryKey({ fetcherName, params }), ...filters });
  };

  useQueryWrapper.getQueryData = (params?: MemberQueryParams<Path, QueryParams>): MemberType | undefined => {
    const queryKey = makeQueryKey({ fetcherName, params });
    return queryClient.getQueryData<MemberType>(queryKey);
  };

  useQueryWrapper.setQueryData = (data: MemberType | null, params?: MemberQueryParams<Path, QueryParams>): void => {
    const queryKey = makeQueryKey({ fetcherName, params });
    queryClient.setQueryData<MemberType>(queryKey, data ?? undefined);
  };

  useQueryWrapper.getQueriesData = (
    params?: Partial<MemberQueryParams<Path, QueryParams>>,
    filters?: Omit<InvalidateQueryFilters, "queryKey">,
  ): [QueryKey, MemberType | undefined][] => {
    return queryClient.getQueriesData<MemberType>({
      queryKey: makeQueryKey({ fetcherName, params }),
      ...filters,
    });
  };

  useQueryWrapper.setQueriesData = (
    updater: ((draft: MemberType) => MemberType | void) | MemberType | null,
    params?: Partial<MemberQueryParams<Path, QueryParams>>,
    filters?: Omit<QueryFilters, "queryKey">,
    options?: SetDataOptions,
  ): void => {
    const searchByQueryKey = makeQueryKey({ fetcherName, params });
    const queriesToUpdate = queryClient
      .getQueryCache()
      .findAll({ queryKey: searchByQueryKey, ...filters }) as Query<MemberType>[];

    const wrappedUpdater = (data: MemberType): MemberType | null => {
      if (updater instanceof Function) {
        return produce(data, updater);
      }
      return updater;
    };

    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: we need to fix this one
    queriesToUpdate.forEach(async (query: Query<MemberType>) => {
      const { state, queryKey } = query;
      const { data, fetchStatus } = state;
      if (!data) return;

      const reduced = wrappedUpdater(data);
      if (isEqual(data, reduced)) return;

      const isFetching = fetchStatus === "fetching";
      if (isFetching) {
        await queryClient.cancelQueries({ queryKey, exact: true });
      }

      queryClient.setQueriesData({ queryKey, exact: true }, reduced, options);

      if (isFetching) {
        await queryClient.refetchQueries({ queryKey, exact: true });
      }
    });
  };

  useQueryWrapper.prefetch = (params: MemberQueryParams<Path, QueryParams>): Promise<void> => {
    const queryKey = makeQueryKey({ fetcherName, params, variedHeaders });
    const parsedUrl = buildUrl({ params, urlPattern }, true);
    const headers = buildHeaders(defaultHeaders, params[CUSTOM_HEADERS]);
    return queryClient.prefetchQuery({
      queryKey,
      queryFn: () => fetchMember(parsedUrl, headers),
      staleTime: Infinity,
    });
  };

  useQueryWrapper.observe = (
    params: MemberQueryParams<Path, QueryParams>,
    options: EnhancedUseQueryOptions<MemberType, MemberQueryParams<Path, QueryParams>> = {},
  ): MemberQueryObserver<MemberType> => {
    const { onSuccess, onError, onSettled, ...extraOptions } = options;
    const parsedUrl = buildUrl({ params, urlPattern }, true);
    const headers = buildHeaders(defaultHeaders, params[CUSTOM_HEADERS]);
    return new QueryObserver<MemberType, Error>(queryClient, {
      queryKey: makeQueryKey({ fetcherName, params, variedHeaders }),
      queryFn: () => fetchMember(parsedUrl, headers),
      enabled: shouldFetchImmediately && Boolean(parsedUrl),
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retryOnMount: false,
      staleTime: Infinity,
      retry: false,
      ...extraConfig,
      ...extraOptions,
      onSuccess: mergeFns(
        (data) => onSuccessConfig?.(data, params),
        (data) => onSuccess?.(data, params),
      ),
      onError: mergeFns(
        (error) => onErrorConfig?.(error, params),
        (error) => onError?.(error, params),
      ),
      onSettled: mergeFns(
        (data, error) => onSettledConfig?.(data, error, params),
        (data, error) => onSettled?.(data, error, params),
      ),
    });
  };

  return useQueryWrapper;
}
